import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import Button from '../../components/Button';
import InputBox from '../../components/InputBox';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { settings, updateSettings, saveSettings, resetSettings, isLoading } = useSettings();
  const { isDark, toggleTheme } = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [activeTab, setActiveTab] = useState('general');

  // Check if user has access to settings page
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        
        if (!token) {
          navigate('/auth/login');
          return;
        }

        const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_BASE_URL}/auth/check-settings-access`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (!data.success || !data.hasAccess) {
          navigate('/auth/login');
          return;
        }

        setIsCheckingAccess(false);
      } catch (error) {
        console.error('Access check failed:', error);
        // Allow access if backend is not running (development mode)
        setIsCheckingAccess(false);
      }
    };

    checkAccess();
  }, [navigate]);

  const handleInputChange = (path, value) => {
    updateSettings(path, value);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    try {
      const result = await saveSettings();
      if (result.success) {
        setSaveMessage('Settings saved successfully!');
      } else {
        setSaveMessage('Failed to save settings');
      }
    } catch (error) {
      setSaveMessage('Error saving settings');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults?')) {
      await resetSettings();
      setSaveMessage('Settings reset to defaults');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const colorPresets = [
    { name: 'TalentGPT Aqua', primary: '#3B5BDB', secondary: '#28ACA8' },
    { name: 'Royal Purple', primary: '#845EF7', secondary: '#F783AC' },
    { name: 'Forest Green', primary: '#40C057', secondary: '#228BE6' },
    { name: 'Sunset Orange', primary: '#F76707', secondary: '#FAB005' },
    { name: 'Cherry Red', primary: '#FA5252', secondary: '#845EF7' },
  ];

  const tabs = [
    { id: 'general', label: 'General', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )},
    { id: 'appearance', label: 'Appearance', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    )},
    { id: 'preview', label: 'Preview', icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    )},
  ];

  if (isCheckingAccess || isLoading) {
    return (
      <div className="bg-gray-100 dark:bg-gray-950 flex items-center justify-center min-h-full">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300 border-t-[var(--color-primary)]"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 dark:bg-gray-950 min-h-full">
      {/* Page Header with Save */}
      <div className="px-6 pt-8 pb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">App Settings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Customize app name, colors, and appearance</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Theme Toggle */}
          <div
            onClick={toggleTheme}
            className="relative w-12 h-6 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer flex-shrink-0 overflow-hidden"
            role="button"
            aria-label="Toggle theme"
          >
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-200 flex items-center justify-center ${isDark ? 'left-6' : 'left-0.5'}`}>
              {isDark ? (
                <svg className="w-3 h-3 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              ) : (
                <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
          <Button onClick={handleSave} loading={isSaving} size="sm">
            Save Changes
          </Button>
        </div>
      </div>

      {/* Success/Error Toast */}
      {saveMessage && (
        <div className="fixed top-20 right-4 z-50 animate-slide-in">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
            saveMessage.includes('success') 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            {saveMessage.includes('success') ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="font-medium">{saveMessage}</span>
          </div>
        </div>
      )}

      <div className="px-6 py-4">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <aside className="lg:w-64 flex-shrink-0">
            <nav className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-gray-50 dark:bg-gray-800 border-l-2'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-2 border-transparent'
                  }`}
                  style={activeTab === tab.id ? { borderLeftColor: settings.theme.colors.primary } : {}}
                >
                  <span className={activeTab === tab.id ? '' : 'text-gray-500 dark:text-gray-400'} style={activeTab === tab.id ? { color: settings.theme.colors.primary } : {}}>
                    {tab.icon}
                  </span>
                  <span className={`font-medium ${activeTab === tab.id ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
                    {tab.label}
                  </span>
                </button>
              ))}
              
              <div className="border-t border-gray-200 dark:border-gray-800">
                <button
                  onClick={handleReset}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="font-medium">Reset to Defaults</span>
                </button>
              </div>
            </nav>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Brand Identity</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Customize your app's name and branding</p>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <InputBox
                        label="App Name"
                        type="text"
                        value={settings.app.name}
                        onChange={(e) => handleInputChange('app.name', e.target.value)}
                        placeholder="Enter app name"
                      />
                      <InputBox
                        label="Tagline"
                        type="text"
                        value={settings.app.tagline}
                        onChange={(e) => handleInputChange('app.tagline', e.target.value)}
                        placeholder="Enter tagline"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                        Logo
                      </label>
                      <div className="flex items-start gap-6">
                        <div 
                          className="w-24 h-24 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${settings.theme.colors.primary}15` }}
                        >
                          <img 
                            src={settings.app.logo} 
                            alt="Logo" 
                            className="w-16 h-16 object-contain"
                            onError={(e) => { e.target.src = '/config/logo.svg'; }}
                          />
                        </div>
                        <div className="flex-1">
                          <InputBox
                            type="text"
                            value={settings.app.logo}
                            onChange={(e) => handleInputChange('app.logo', e.target.value)}
                            placeholder="/config/logo.svg"
                          />
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Enter the path to your logo file (SVG recommended for best quality)
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-6">
                {/* Color Presets */}
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Color Themes</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Choose a preset or customize your colors</p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {colorPresets.map((preset) => (
                        <button
                          key={preset.name}
                          onClick={() => {
                            handleInputChange('theme.colors.primary', preset.primary);
                            handleInputChange('theme.colors.secondary', preset.secondary);
                          }}
                          className={`relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all hover:scale-105 ${
                            settings.theme.colors.primary === preset.primary
                              ? 'border-gray-900 dark:border-white shadow-md'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          {settings.theme.colors.primary === preset.primary && (
                            <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gray-900 dark:bg-white flex items-center justify-center">
                              <svg className="w-3 h-3 text-white dark:text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                          <div className="flex gap-1">
                            <div className="w-6 h-6 rounded-full shadow-inner" style={{ backgroundColor: preset.primary }} />
                            <div className="w-6 h-6 rounded-full shadow-inner" style={{ backgroundColor: preset.secondary }} />
                          </div>
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Custom Colors</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Fine-tune individual color values</p>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {Object.entries(settings.theme.colors).map(([colorName, colorValue]) => (
                        <div key={colorName} className="group">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">
                            {colorName}
                          </label>
                          <div className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 group-hover:border-gray-300 dark:group-hover:border-gray-600 transition-colors">
                            <div className="relative w-8 h-8 flex-shrink-0">
                              <div 
                                className="w-8 h-8 rounded-md border border-gray-300 dark:border-gray-600 shadow-inner"
                                style={{ backgroundColor: colorValue }}
                              />
                              <input
                                type="color"
                                value={colorValue}
                                onChange={(e) => handleInputChange(`theme.colors.${colorName}`, e.target.value)}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              />
                            </div>
                            <input
                              type="text"
                              value={colorValue.toUpperCase()}
                              onChange={(e) => handleInputChange(`theme.colors.${colorName}`, e.target.value)}
                              className="flex-1 bg-transparent text-sm font-mono text-gray-700 dark:text-gray-300 focus:outline-none uppercase"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Tab */}
            {activeTab === 'preview' && (
              <div className="space-y-6">
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Live Preview</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">See how your settings will look</p>
                  </div>
                  
                  <div className="p-6">
                    {/* App Header Preview */}
                    <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                      <div 
                        className="px-6 py-4"
                        style={{ background: `linear-gradient(135deg, ${settings.theme.colors.primary}, ${settings.theme.colors.secondary})` }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                            <img 
                              src={settings.app.logo} 
                              alt="Logo" 
                              className="w-6 h-6 object-contain brightness-0 invert"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                          <div>
                            <h3 className="font-bold text-white text-lg">{settings.app.name}</h3>
                            <p className="text-sm text-white/80">{settings.app.tagline}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="p-6 bg-gray-50 dark:bg-gray-800">
                        {/* Button Variants */}
                        <div className="mb-6">
                          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Buttons</h4>
                          <div className="flex flex-wrap gap-3">
                            <Button variant="primary">Primary</Button>
                            <Button variant="secondary">Secondary</Button>
                            <Button variant="outline">Outline</Button>
                            <Button variant="text">Text</Button>
                          </div>
                        </div>
                        
                        {/* Input Preview */}
                        <div className="mb-6">
                          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Input Fields</h4>
                          <div className="max-w-sm">
                            <InputBox 
                              placeholder="Sample input field" 
                              label="Sample Label"
                            />
                          </div>
                        </div>
                        
                        {/* Color Palette Display */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Color Palette</h4>
                          <div className="flex gap-2">
                            {Object.entries(settings.theme.colors).slice(0, 4).map(([name, color]) => (
                              <div key={name} className="flex flex-col items-center gap-1">
                                <div 
                                  className="w-12 h-12 rounded-lg shadow-sm"
                                  style={{ backgroundColor: color }}
                                />
                                <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </main>
        </div>
      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SettingsPage;
